import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Rocket, Play } from "lucide-react";
import { WHATSAPP } from "@/lib/brand";
import { trackContactClick, trackSectionAnchor } from "@/lib/analytics";

/**
 * Hero copy only — the globe lives at page level (HomeBackdrop) so it spans
 * the entire home experience, not just this section.
 */
const HeroSection = () => {
    const { t } = useTranslation();
    const whatsappUrl = `${WHATSAPP.sy.url}?text=${encodeURIComponent(
        t("common.whatsapp_initial_project"),
    )}`;

    return (
        <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden pt-28 pb-12">
            <div className="container mx-auto px-6 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-v2-label text-white/60 mb-10">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                        {t("hero.badge")}
                    </div>

                    <h1
                        className="font-v2-hero text-white mb-8"
                        style={{ fontSize: "clamp(56px, 7vw, 120px)" }}
                    >
                        {t("hero.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("hero.title_highlight")}
                        </span>
                        <br />
                        {t("hero.title_suffix")}
                    </h1>

                    <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-12 leading-relaxed font-medium">
                        {t("hero.subtitle")}
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-cursor="hover"
                            onClick={() => trackContactClick("whatsapp_sy", "home_hero")}
                        >
                            <Button
                                size="lg"
                                className="h-14 px-8 text-base rounded-full text-white font-bold gap-2 border-0"
                                style={{
                                    background: "var(--gradient-accent-v2)",
                                    boxShadow: "0 12px 32px -8px hsl(260 100% 55% / 0.5)",
                                }}
                            >
                                <Rocket className="w-5 h-5" />
                                {t("hero.cta_primary")}
                            </Button>
                        </a>

                        <a
                            href="#flagships"
                            data-cursor="hover"
                            onClick={() => trackSectionAnchor("flagships", "home_hero")}
                        >
                            <Button
                                size="lg"
                                variant="outline"
                                className="h-14 px-8 text-base rounded-full border-white/15 hover:bg-white/5 text-white font-bold gap-2"
                            >
                                <Play className="w-4 h-4" />
                                {t("hero.cta_secondary")}
                            </Button>
                        </a>
                    </div>
                </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 pointer-events-none">
                <span className="font-v2-label text-[10px] text-white/40">{t("hero.scroll")}</span>
                <span className="block w-px h-8 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
            </div>
        </section>
    );
};

export default HeroSection;
